<script lang="ts" setup>
import { SancareToggle } from '@sancare/typescript-commons'
import dayjs from 'dayjs'
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import McoStayEditionRow from '@/maintenance/McoStayEditionRow.vue'
import SmrStayEditionRow from '@/maintenance/SmrStayEditionRow.vue'
import type { StayEdition } from '@/maintenance/types'
import { useStore } from '@/store'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()
const props = defineProps<{
  pmsiType: PmsiTypeEnum
  pmsiId: string
}>()

const { t } = useI18n()

const prefs = computed(() => store.state.maintenance.prefs.stayEditions)
const stayEditions = computed(() => store.state.maintenance.stayEditions)

const stayEditionsByDay = computed<Record<string, StayEdition[]>>(() => {
  if (!Array.isArray(stayEditions.value)) return {}
  return stayEditions.value.reduce((acc: Record<string, StayEdition[]>, edition: StayEdition) => {
    const dayKey = edition.creationDate ? dayjs(edition.creationDate).format('DD/MM/YYYY') : 'Inconnu'
    if (!acc[dayKey]) acc[dayKey] = []
    acc[dayKey].push(edition)
    return acc
  }, {})
})

const stayEditionsRequest = computed(() => store.state.maintenance.requests.getStayEditionRequest)

onMounted(refresh)
watch(() => [props.pmsiId, props.pmsiType], refresh)

function refresh() {
  if ((stayEditionsRequest.value.fetching || stayEditionsRequest.value.ok)) {
    return
  }

  store.dispatch('getStayEditions', { pmsiType:  props.pmsiType, pmsiId: props.pmsiId })
}

async function setPref(key: string, value:string) {
  store.commit('updatePrefs', { prefArea: 'stayEditions', prefKey: key, prefValue: value })
  await store.dispatch('getStayEditions', { pmsiId: props.pmsiId, pmsiType: props.pmsiType })
}

</script>

<template>
  <div>
    <div class="d-flex justify-content-between mb-4 mt-2">
      <div />
      <sancare-toggle
        :value="prefs.seeMoreEventsGrouping"
        :label="'Voir tout'"
        @toggle-change="(moreGrouping) => setPref('seeMoreEventsGrouping', moreGrouping)"
      />
    </div>
    <div
      v-if="stayEditionsRequest.fetching"
      class="loader loader-lg loader-center mb-4"
    />
    <div
      v-else-if="!stayEditionsRequest.ok || stayEditionsRequest.error"
    >
      Erreur: {{ stayEditionsRequest.error }}
    </div>
    <div
      v-else-if="stayEditions.length === 0"
    >
      Aucun historique à afficher pour l'identifiant {{ pmsiType === PmsiTypeEnum.MCO ? 'RSS' : 'SMR' }} {{ pmsiId }}
    </div>
    <div
      v-else
      class="row"
    >
      <div class="col-12">
        <template
          v-for="([day, editions]) in Object.entries(stayEditionsByDay)"
          :key="day"
        >
          <div class="sticky-top day-banner mb-2">
            <span class="day-label">{{ day }}</span>
          </div>
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>{{ t('maintenance.stayHistory.headers.date') }}</th>
                <th>{{ t('maintenance.stayHistory.headers.type') }}</th>
                <th>{{ t('maintenance.stayHistory.headers.reference') }}</th>
                <th>{{ t('maintenance.stayHistory.headers.mode') }}</th>
                <th>{{ t('maintenance.stayHistory.headers.author') }}</th>
                <template v-if="pmsiType === PmsiTypeEnum.SMR">
                  <th>{{ t('maintenance.stayHistory.headers.medicalUnit') }}</th>
                  <th>{{ t('maintenance.stayHistory.headers.weekNb') }}</th>
                </template>
                <th>{{ pmsiType === PmsiTypeEnum.SMR ? t('maintenance.stayHistory.headers.gme') : t('maintenance.stayHistory.headers.ghm') }}</th>
                <th>{{ t('maintenance.stayHistory.headers.value') }}</th>
              </tr>
            </thead>
            <tbody>
              <component
                :is="pmsiType === PmsiTypeEnum.MCO ? McoStayEditionRow : SmrStayEditionRow"
                v-for="(stayEdition, index) in editions"
                :key="index"
                :stay-edition="stayEdition"
              />
            </tbody>
          </table>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gap-4 > *:not(:last-child) {
  margin-right: 2rem;
}

.sticky-top.day-banner {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #eeeeee;
  border-bottom: 1px solid #d1d5db;
  padding: .5rem 1rem;
  display: flex;
  align-items: center;
}

.day-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #222222;
  letter-spacing: .01em;
}
</style>
